import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX } from 'lucide-react';

// Images des 7 étapes de préparation du biberon - Photos réalistes
const BIBERON_STEPS = [
  {
    id: 1,
    title: "Étape 1 : Se laver les mains",
    description: "Lavage soigneux au savon pendant 30 secondes minimum",
    image: "https://static.prod-images.emergentagent.com/jobs/0bdb7109-8029-4d81-a486-71beb721288a/images/47039cf59cbffef3bce1111acd035275889ffe2f4f2ce3862772efaf3ccf8b7c.png"
  },
  {
    id: 2,
    title: "Étape 2 : Préparer le plan de travail",
    description: "Surface propre et désinfectée, matériel à portée de main",
    image: "https://static.prod-images.emergentagent.com/jobs/0bdb7109-8029-4d81-a486-71beb721288a/images/f7935962dfcba128ae17db82705cb55919b76a034608dce5d2de89b5f692273a.png"
  },
  {
    id: 3,
    title: "Étape 3 : Verser l'eau en premier",
    description: "Eau à température ambiante ou tiédie (37°C max)",
    image: "https://static.prod-images.emergentagent.com/jobs/0bdb7109-8029-4d81-a486-71beb721288a/images/b0dfeb21b1048025a3f7b2a9f1f8468d2a0b29636991d584522338c8c3b2fea5.png"
  },
  {
    id: 4,
    title: "Étape 4 : Ajouter la poudre",
    description: "Mesurettes rases, araser avec le rebord de la boîte de lait",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/7e695dc1aa2f78ecba022c7d2eb55b7f5f7993e48585b857714b3f897562b85f.png"
  },
  {
    id: 5,
    title: "Étape 5 : Fermer et agiter",
    description: "Mouvements circulaires pour bien dissoudre la poudre",
    image: "https://static.prod-images.emergentagent.com/jobs/0bdb7109-8029-4d81-a486-71beb721288a/images/54bdfdbaaf91d1b6b816e18fe4c06e22d8d0c90bcc83a445bf010ab5b0cfe699.png"
  },
  {
    id: 6,
    title: "Étape 6 : Vérifier la température",
    description: "Quelques gouttes sur l'intérieur du poignet (tiède = bon)",
    image: "https://static.prod-images.emergentagent.com/jobs/0bdb7109-8029-4d81-a486-71beb721288a/images/1aabeb009823ffd6115bad67770b11654463608746350729fd1e21235da5f6ec.png"
  },
  {
    id: 7,
    title: "Étape 7 : Donner immédiatement",
    description: "Le biberon doit être consommé dans l'heure",
    image: "https://static.prod-images.emergentagent.com/jobs/0bdb7109-8029-4d81-a486-71beb721288a/images/97abe61ab0b912004ffed6fab7a6ca26c2821d1562510c6e992e9f4eacec2358.png"
  }
];

// Logo APDR complet
const APDR_LOGO_FULL = "https://customer-assets.emergentagent.com/job_0bdb7109-8029-4d81-a486-71beb721288a/artifacts/o3wx8hx1_logo%20apdr%20formation.png";

const BiberonCarousel = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  const BACKGROUND_MUSIC = "https://cdn.pixabay.com/audio/2022/10/25/audio_850edcfd29.mp3";

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentStep((prev) => (prev + 1) % BIBERON_STEPS.length);
      }, 5000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      if (isPlaying && !isMuted) {
        audioRef.current.play().catch(() => {});
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, isMuted]);

  const handlePrev = () => {
    setCurrentStep((prev) => (prev - 1 + BIBERON_STEPS.length) % BIBERON_STEPS.length);
  };

  const handleNext = () => {
    setCurrentStep((prev) => (prev + 1) % BIBERON_STEPS.length);
  };

  const step = BIBERON_STEPS[currentStep];

  return (
    <div 
      className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-pink-200"
      style={{ minHeight: '480px' }}
    >
      <audio ref={audioRef} loop>
        <source src={BACKGROUND_MUSIC} type="audio/mpeg" />
      </audio>

      {/* Fond avec logo APDR */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${APDR_LOGO_FULL})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.08
        }}
      />

      {/* Overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(252,231,243,0.88) 100%)'
        }}
      />

      {/* Header */}
      <div className="relative z-10 bg-gradient-to-r from-pink-500 to-pink-400 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white p-1 shadow-lg">
              <img src={APDR_LOGO_FULL} alt="APDR" className="w-full h-full object-contain" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Formation APDR de la VAE</h3>
              <p className="text-pink-100 text-sm">Préparation du biberon - 7 étapes</p>
            </div>
          </div>
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold">
            {currentStep + 1} / {BIBERON_STEPS.length}
          </span>
        </div>
      </div>

      {/* Contenu */}
      <div className="relative z-10 p-6">
        <div className="flex flex-col md:flex-row gap-6 items-center">
          {/* Image */}
          <div className="flex-1 flex justify-center">
            <div className="relative">
              <img 
                src={step.image} 
                alt={step.title}
                className="w-72 h-72 object-cover rounded-2xl shadow-xl border-4 border-white"
                key={step.id}
                style={{ animation: 'fadeIn 0.5s ease-in-out' }}
              />
              <div className="absolute -top-3 -left-3 bg-gradient-to-br from-pink-500 to-pink-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl shadow-lg border-2 border-white">
                {step.id}
              </div>
            </div>
          </div>

          {/* Texte */}
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold text-pink-600 mb-3">{step.title}</h2>
            <p className="text-gray-700 text-lg mb-6 bg-white/60 p-3 rounded-lg">{step.description}</p>

            {/* Indicateurs */}
            <div className="flex gap-2 justify-center md:justify-start">
              {BIBERON_STEPS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    index === currentStep ? 'bg-pink-500 w-8' : 'bg-pink-200 hover:bg-pink-300 w-3'
                  }`}
                  data-testid={`carousel-dot-${index}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contrôles */}
      <div className="relative z-10 bg-white/90 backdrop-blur-sm p-4 flex items-center justify-between border-t border-pink-100">
        <button
          onClick={handlePrev}
          className="flex items-center gap-2 px-4 py-2 bg-pink-100 hover:bg-pink-200 text-pink-600 rounded-full transition-all font-medium"
          data-testid="carousel-prev"
        >
          <ChevronLeft size={20} />
          Précédent
        </button>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-lg ${
              isPlaying ? 'bg-pink-500 text-white' : 'bg-white text-pink-500 border-2 border-pink-500'
            }`}
            data-testid="carousel-play"
          >
            {isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-1" />}
          </button>

          <button
            onClick={() => setIsMuted(!isMuted)}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
              isMuted ? 'bg-gray-200 text-gray-500' : 'bg-pink-100 text-pink-500'
            }`}
            data-testid="carousel-mute"
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        </div>

        <button
          onClick={handleNext}
          className="flex items-center gap-2 px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-full transition-all font-medium"
          data-testid="carousel-next"
        >
          Suivant
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Barre de progression */}
      <div className="relative z-10 h-2 bg-pink-100">
        <div 
          className="h-full bg-gradient-to-r from-pink-400 to-pink-500 transition-all duration-300"
          style={{ width: `${((currentStep + 1) / BIBERON_STEPS.length) * 100}%` }}
        />
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default BiberonCarousel;
